import { Injectable } from '@angular/core';
import { EventApi, EventInput } from '@fullcalendar/common';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, combineLatest, Subject } from 'rxjs';
import { GraphQLFilter } from '../../../shared/components/graphql-filter/graphql-filter.component';
import { UnscheduledTaskSubscription, GetCalendarSchedulableTasksQuery } from '../../../API.service';
import { JsonService } from '../../../services/helpers/json/json.service';
import { ViewService } from '../../../services/view.service';
import { ClientStoreService } from '../../../services/client-store.service';
import { clone, cloneDeep, get, isEmpty, isObject } from 'lodash-es';
import { sprintf } from 'sprintf-js';
import { ReadableAddressPipe } from '../../../pipes/readable-address.pipe';
import { TaskMetadataC } from '../full-calendar/classes/metadata/task-metadata';
import { JobMetadata, JobMetadataC } from '../full-calendar/classes/metadata/job-metadata';
import { OpportunityMetadata, OpportunityMetadataC } from '../full-calendar/classes/metadata/opportunity-metadata';
import { ActivityLogTypeMetadata } from '../full-calendar/classes/metadata/activity-log-type-metadata';
import { LooseObject } from '../../../objects/loose-object';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { CalendarApiService } from './calendar-api.service';
import { NotificationService } from '../../../services/notification.service';
import { filter, switchMap, tap } from 'rxjs/operators';
import { blank, data_get, filled, when } from '../../../shared/utils/common';
import pluralize from 'pluralize';
import { CustomCurrencyPipe } from '../../../pipes/custom-currency-pipe';
import { LocalStorageService } from '../../../services/local-storage.service';

@Injectable()
export class CalendarService {

  /**
   * Filtration object that will be passed to our GetCalendarSchedulableTasks
   * GrahpQL query which affects the list of schedulable tasks.
   *
   * @type {GraphQLFilter}
   */
  schedulableTasksFilter: GraphQLFilter | null = null;

  /**
   * Are the users list still being fetched?
   *
   * @type {BehaviorSubject<boolean>}
   */
  resourcesAreLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Are the tasks that will be displayed for the current date
   * (displayed in the calendar) still being fetched?
   *
   * @type {BehaviorSubject<boolean>}
   */
  eventsAreLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Is the list of tasks that are available for scheduling, still being fetched?
   *
   * @type {BehaviorSubject<boolean>}
   */
  schedulableTasksAreLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * to determine if calendar is loaded
   *
   * @type {BehaviorSubject<boolean>}
   */
  calendarAreLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Behavior subject for the calendar module that determines its loading state.
   *
   * @type {Observable<boolean[]>}
   */
  isCalendarDataLoading$: Observable<boolean[]> = combineLatest(
    this.resourcesAreLoading,
    this.eventsAreLoading,
    this.schedulableTasksAreLoading,
    this.calendarAreLoading,
  );

  /**
   * Behavior subject for checking the list of tasks.
   *
   * @type {BehaviorSubject<{id: string, value: string}[] | boolean>}
   */
  reloadSchedulableTasks = new BehaviorSubject<object>({});

  /**
   * Subject for triggering the closing of the fullscreen view in the parent dialog
   *
   * @type {Subject<void>}
   */
  closeFullscreenCalendar: Subject<void> = new Subject<void>();

  /**
   * Subject for triggering the switching of the calendar view from Team to Task
   *
   * @type {Subject<void>}
   */
  toggleCalendarView: Subject<boolean> = new Subject<boolean>();

  /**
   * Subject for triggering update of calendar when
   * scheduling, unscheduling, duplicating, or editing
   * task in dialog.
   *
   * @type {Subject<object>}
   */
  updateCalendar: Subject<object> = new Subject<object>();

  /**
   * Observable for updateCalendar.
   *
   * @type {Subject<object>}
   */
  calendarUpdate$: Observable<object> = this.updateCalendar.asObservable();

  /**
   * Subject for triggering update of tasks/jobs list for scheduling when
   * scheduling, unscheduling, or editing
   * task in dialog.
   *
   * @type {Subject<object>}
   */
  updateForSchedulingList: Subject<object> = new Subject<object>();

  /**
   * Observable for updateForSchedulingList.
   *
   * @type {Subject<object>}
   */
  forSchedulingListUpdate$: Observable<object> = this.updateForSchedulingList.asObservable();

  strRcordMetadataType: string = null;

  /**
   * Subject for triggering the switching of the calendar view
   *
   * @type {Subject<'teams' | 'users' | 'resources'>}
   */
  changeCalendarView: Subject<'users' | 'teams' | 'resources'> = new Subject<'users' | 'teams' | 'resources'>();

  /**
   * Subject for triggering the switching of the calendar view
   *
   * @type {Subject<'teams' | 'users' | 'resources'>}
   */
  changeCalendarMode: Subject<'simple' | 'full' | 'resources'> = new Subject<'simple' | 'full' | 'resources'>();

  /**
   * Adjust the total count after scheduling/unscheduling without
   * having to refetch the tasks/jobs for scheduling list.
   *
   * @type {number}
   */
  private totalChangeOffset: number = 0;

  /**
   * Adjust the 'showing to' count after scheduling/unscheduling without
   * having to refetch the tasks/jobs for scheduling list.
   *
   * @type {number}
   */
  private showingToChangeOffset: number = 0;

  /**
   * Returns the totalChangeOffset value to display the latest
   * number of entries in the tasks/jobs for scheduling list.
   *
   * @returns {number}
   */
  get totalChange(): number {
    return this.totalChangeOffset;
  }

  /**
   * Returns the showingToChangeOffset value to display the latest
   * number remaining items in the current page of the
   * tasks/jobs for scheduling list.
   *
   * @returns {number}
   */
  get showingToChange(): number {
    return this.showingToChangeOffset;
  }

  constructor(
    protected translateService: TranslateService,
    protected jsonService: JsonService,
    protected readableAddressPipe: ReadableAddressPipe,
    protected clientStoreService: ClientStoreService,
    protected viewService: ViewService,
    protected router: Router,
    protected _location: Location,
    protected _api: CalendarApiService,
    protected _notifications: NotificationService,
    protected localStorageService: LocalStorageService
  ) { }

  /**
   * Returns task metadata from the given FullCalendar
   * event object.
   *
   * FullCalendar's external event objects returns custom
   * attributes in the following manner: `object.event.extendedProps`
   * and since our own task metadata is not returned in this
   * way, we end up having two (2) ways for accessing a task's
   * metadata from within a FullCalendar event object. This
   * method will help in retrieving said metadata.
   *
   * @param event
   *
   * @returns {{string}}
   */
  getTaskMetadata(event: EventApi | EventInput | any): any {
    if (event.taskMetadata !== undefined) {
      return event.taskMetadata;
    } else if (event.extendedProps !== undefined) {
      return event.extendedProps.taskMetadata;
    } else if (event.event !== undefined) {
      return event.event.extendedProps.taskMetadata
    }
  }

  /**
   * Returns task metadata from the given FullCalendar
   * event object.
   *
   * FullCalendar's external event objects returns custom
   * attributes in the following manner: `object.event.extendedProps`
   * and since our own task metadata is not returned in this
   * way, we end up having two (2) ways for accessing a task's
   * metadata from within a FullCalendar event object. This
   * method will help in retrieving said metadata.
   *
   * @param event
   *
   * @returns {{string}}
   */
  getRecordMetadata(event: EventApi | EventInput | any): TaskMetadataC | JobMetadataC | ActivityLogTypeMetadata  {
    if (event.recordMetadata !== undefined) {
      this.strRcordMetadataType = event.recordMetadata.metadata_type || null;
      return event.recordMetadata;
    } else if (event.extendedProps !== undefined) {
      this.strRcordMetadataType = event.extendedProps.recordMetadata.metadata_type || null;
      return event.extendedProps.recordMetadata;
    } else if (event.event !== undefined) {
      this.strRcordMetadataType = event.event.extendedProps.recordMetadata.metadata_type || null;
      return event.event.extendedProps.recordMetadata;
    }
  }

  getRecordMetadataType(): string | null {
    return this.strRcordMetadataType || null
  }

  getEventData(objEvent: any): TaskMetadataC | JobMetadataC | OpportunityMetadataC | ActivityLogTypeMetadata {
    let objEventData = objEvent.recordMetadata
      || get(objEvent, ['extendedProps', 'recordMetadata'])
      || get(objEvent, ['event', 'extendedProps', 'recordMetadata']);

    if (objEventData.metadata_type == 'task') {
      return new TaskMetadataC(objEventData);
    } else if (objEventData.metadata_type == 'job') {
      return new JobMetadataC(objEventData);
    } else if (objEventData.metadata_type === 'opportunity') {
      return new OpportunityMetadataC(objEventData);
    }

    return objEventData;
  }

  /**
   * Formats the taskmetadata so that it can be properly displayed
   * by the dialog box.
   *
   * @param {Object} taskMetadata
   *
   * @returns {Object}
   */
  formatTaskForDialogDisplay(taskMetadata): object {
    let humanReadableDateFormat = 'lll';
    let taskMetadataClone = clone(taskMetadata);

    taskMetadataClone['due_date'] = taskMetadataClone['due_date'] !== null ? moment(taskMetadataClone['due_date']).format(humanReadableDateFormat) : null;
    taskMetadataClone['date_completed'] = taskMetadataClone['date_completed'] !== null ? moment(taskMetadataClone['date_completed']).format(humanReadableDateFormat) : null;

    switch (taskMetadataClone['priority']) {
      case '1':
        taskMetadataClone['priority'] = 'high';
        break;
      case '2':
        taskMetadataClone['priority'] = 'medium';
        break;
      case '3':
        taskMetadataClone['priority'] = 'low';
        break;
    }

    const strTaskType: string = taskMetadata['metadata_type'] || '';

    if (strTaskType === 'job' || strTaskType === 'opportunity') {
      taskMetadataClone[strTaskType] = {
        id: taskMetadataClone['id'],
        full_address: taskMetadataClone['full_address'],
        [`${strTaskType}_number`]: taskMetadataClone[`${strTaskType}_number`]
      }
    }

    return taskMetadataClone;
  }

  /**
   * Formats the given list of tasks so that it can be properly displayed
   * in the list of schedulable tasks.
   *
   * @param tasks
   *
   * @returns {UnscheduledTaskSubscription[] | GetCalendarSchedulableTasksQuery[]}
   */
  formatTaskForListDisplay(tasks): UnscheduledTaskSubscription[] | GetCalendarSchedulableTasksQuery[] {

    // FC-1378: Cannot read property 'map'
    tasks = tasks || [];
    return JSON.parse(JSON.stringify(tasks)).map((task) => {
      // As of writing, we only modify tasks to return a user's full name.
      // We do this just so that we don't concatenate the first and last name
      // repeatedly everywhere.
      let first_name = (task.user_first_name !== '' && task.user_first_name !== null) ? task.user_first_name : '';
      let last_name = (task.user_last_name !== '' && task.user_last_name !== null) ? task.user_last_name : '';
      task.user_full_name = `${first_name} ${last_name}`;

      return task;
    });
  }

  formatTooltip(strMetadataType, objMetadata): string {
    if (strMetadataType === 'activity_log_type') {
      return 'test';
    } else {
      return this.formatTaskTooltip(objMetadata, objMetadata.departments)
    }
  }

  /**
   * Gets task data and formats to be
   * displayed on task's tooltip
   *
   * @todo: Add design to task's tooltip
   * @returns {string|undefined}
   */
  formatTaskTooltip(taskMetadata, taskDepartments) : string|undefined {

    if (get(taskMetadata, ['metadata_type']) === 'task') {
      const strDefaultEmptyValue = '--';
      const strTaskModule: 'job' | 'opportunity' = get(taskMetadata, 'job') ? 'job' : 'opportunity';
      const strDeletedMessage: string = strTaskModule === 'job' ? 'calendar_job_deleted' : 'calendar_quote_deleted';

      let strTaskPriority = strDefaultEmptyValue;
      let strDepartmentName = get(taskMetadata, ['department', 'name'], strDefaultEmptyValue);
      let strTaskDescription = taskMetadata['description'] || strDefaultEmptyValue;
      let address = get(taskMetadata, [strTaskModule, 'full_address'], strDefaultEmptyValue);

      if (taskMetadata['priority'] && taskMetadata['priority'] !== null) {
        strTaskPriority = this.translateService.instant(taskMetadata['priority']);
      }

      let strCustomerName = get(taskMetadata, [strTaskModule, 'customer', 'name'], strDefaultEmptyValue);
      let strToolTipHeader = (taskMetadata[strTaskModule] && taskMetadata[strTaskModule][`${strTaskModule}_number`]) ? '' : this.translateService.instant(strDeletedMessage)+ `\n`;

      return sprintf(
        "%s%s: %s\n%s: %s\n%s: %s\n%s: %s\n%s: %s\n%s: %s",
        strToolTipHeader,
        this.translateService.instant('name'), taskMetadata['name'],
        this.translateService.instant('customer_name'), strCustomerName,
        this.translateService.instant('department'), strDepartmentName,
        this.translateService.instant('priority'), strTaskPriority,
        this.translateService.instant('task_description'), strTaskDescription,
        this.translateService.instant('address'), address || '--',
      );
    }
  }

  /**
   * Decrements the 'Showing x to y of z entries' values
   * in the tasks/jobs for scheduling list.
   *
   * @returns {void}
   */
  decrementListingOffsets(): void {
    this.totalChangeOffset -= 1;
    this.showingToChangeOffset -= 1;
  }

  /**
   * Increments the 'Showing x to y of z entries' values
   * in the tasks/jobs for scheduling list.
   *
   * @param {number} numListLength
   *
   * @returns {void}
   */
  incrementListingOffsets(numListLength: number): void {
    if (numListLength < 10) {
      this.showingToChangeOffset += 1;
    }

    this.totalChangeOffset += 1;
  }

  /**
   * Resets the 'Showing x to y of z entries' values
   * in the tasks/jobs for scheduling list.
   *
   * @returns {void}
   */
  resetListingOffsets(): void {
    this.totalChangeOffset = 0;
    this.showingToChangeOffset = 0;
  }

  /**
   * Create a query params for related customer module
   *
   * @param {bIsBehalfOfChildClient} boolean
   *
   * @returns {Record<string, string>}
   */
  protected makeRelateCustomerQueryParams(bIsBehalfOfChildClient: boolean): Record<string, string> {
    const onBehalfOfClientId = get(this.viewService.getRecord().record_details, 'customer_owner_client_id');
    const currentClientId = this.clientStoreService.getActiveClient().client_id;

    if (onBehalfOfClientId !== currentClientId && !bIsBehalfOfChildClient) {
      return {
        on_behalf_of: onBehalfOfClientId,
      };
    }

    return {};
  }

  /**
   * Creates the URL for links to other modules
   *
   * @param {string} moduleName
   * @param {string|undefined} moduleId
   * @param {bIsBehalfOfChildClient} boolean
   *
   * @returns {string}
   */
  createUrl(moduleName: string, moduleId: string | undefined = undefined, bIsBehalfOfChildClient: boolean): string {
    let navigateCommand = [`/${moduleName}/${moduleId}`];
    let navigationExtras = {
      queryParams: {
        ... (moduleName === 'customers' && this.makeRelateCustomerQueryParams(bIsBehalfOfChildClient))
      }
    }
    let url = this.router.serializeUrl(this.router.createUrlTree(navigateCommand, navigationExtras));

    return this._location.prepareExternalUrl(url);
  }

  /**
   * Gets the opportunity details of the task.
   *
   * @returns {OpportunityMetadata}
   */
  getTaskOpportunityDetails(objTaskMetadata: LooseObject, objRecordDetails: LooseObject, arTasks = []): OpportunityMetadata {
    return {
      id: objTaskMetadata['opportunity_id'],
      opportunity_number: objRecordDetails['opportunity_number'],
      forecast_close_date: objRecordDetails['forecast_close_date'],
      full_address: objRecordDetails['address_text'],
      customer: {
        id: objRecordDetails['customer_id'],
        name: objRecordDetails['customer_text'],
      },
      tasks: arTasks
        ? arTasks.filter(task => task.id !== objTaskMetadata['id'])
        : null,
      assigned_user: !isEmpty(objRecordDetails['user_id'])
        ? { id: objRecordDetails['user_id'], name: objRecordDetails['user_text'] }
        : null,
      department: !isEmpty(objRecordDetails['department_id'])
        ? { id: objRecordDetails['department_id'], name: objRecordDetails['department_text'] }
        : null,
      is_deleted: objTaskMetadata.is_deleted || false
    }
  }

  /**
   * Gets the job details of the task.
   *
   * @returns {JobMetadata}
   */
  getTaskJobDetails(objTaskMetadata: LooseObject, objRecordDetails: LooseObject, arTasks = []): JobMetadata {
    return {
      id: objTaskMetadata['job_id'],
      job_number: objRecordDetails['job_number'],
      due_date: objRecordDetails['due_date'],
      full_address: objRecordDetails['address_text'],
      status: objRecordDetails['status'],
      customer: {
        id: objRecordDetails['customer_id'],
        name: objRecordDetails['customer_text'],
      },
      tasks: arTasks
        ? arTasks.filter(task => task.id !== objTaskMetadata['id'])
        : null,
      assigned_user: !isEmpty(objRecordDetails['user_id'])
        ? { id: objRecordDetails['user_id'], name: objRecordDetails['user_text'] }
        : null,
      department: !isEmpty(objRecordDetails['department_id'])
        ? { id: objRecordDetails['department_id'], name: objRecordDetails['department_text'] }
        : null,
      is_deleted: objTaskMetadata.is_deleted || false
    }
  }

  unscheduleTask(opts: {
    task: Record<string, any>,
    record_details: Record<string, any>,
    module: string,
    tasks: any, /// no idea wtf is this
    onComplete: (task?: UnscheduleTaskResponse) => void,
    on_behalf_of_child_client?: boolean,
    child_client_id?: string,
  }): void {
    if (blank(get(opts, 'task.id'))) {
      return;
    }

    this._notifications.sendConfirmation('Are you sure you want to unschedule this task?')
      .pipe(
        filter(({answer}) => answer),
        switchMap(() => this._api.unscheduleTask(get(opts, 'task.id'), {
          ... (opts.on_behalf_of_child_client && {
            for_child_client_id: opts.child_client_id,
          })
        })),
        tap({
          next: () => this._notifications.notifySuccess('task_schedule_cancelled'),
          error: () => {
            this._notifications.notifyError('task_schedule_cancel_error');
            opts.onComplete();
          },
        }),
      )
      .subscribe((task) => {
        // we would use the task data from the api response
        // when a parent client cancels its own task in the child client
        // system. This is necessary so we could append the task
        // to the list of task to be schedule just like the normal
        // unscheduling of task
        const data: TaskMetadataC = (task['was_replaced']) ? task : {
          id: get(opts.task, 'id'),
          name: get(opts.task, 'activity_name'),
          status: 'awaiting_scheduling',
          due_date: null,
          priority: get(opts.task, 'priority'),
          estimated_duration: 0,
          description: get(opts.task, 'notes'),
          job: when(opts.module === 'jobs', {
            then: () => this.getTaskJobDetails(opts.task, opts.record_details, opts.tasks),
          }),
          opportunity: when(opts.module === 'opportunities', {
            then: () => this.getTaskOpportunityDetails(opts.task, opts.record_details, opts.tasks),
          }),
          department: {
            id: get(opts.task, 'department_id'),
            name: get(opts.task, 'department_name'),
          },
          assigned_user: null,
          viewable: false,
          parent_id: get(opts.task, 'parent_id'),
          metadata_type: 'task',
          activity_date: null,
        };

        opts.onComplete({
          action: 'unschedule',
          data,
        });
      });
  }

  /**
   * Simply checks if it's currently DST and subtracts
   * an hour from the date.
   *
   * @param {string} strDate
   *
   * @returns {string}
   */
  checkDSTAndModifyDate(strDate: string, bToUtc: boolean = false): string {
    if (bToUtc) {
      return moment.utc(strDate).format();
    } else {
      return moment(strDate).format();
    }
  }

  private translatedValue: LooseObject = {};

  private moduleMetadata: LooseObject = {};

  /**
   * compile the dropdown values
   *
   * @param dropdown
   */
  setDropdownTranslatedValues(dropdown: LooseObject) : void {
    Object.keys(dropdown).forEach(module => {
      dropdown[module].forEach( option => {
        this.translatedValue[`${module}.${option.dropdown_field}.${option.key}`] = filled(option.name) ? this.translateService.instant(option.name) : '';
      });
    });
  }

  /**
   * store module metadata
   */
  setModuleMetadata(metadata: LooseObject) : void {
    this.moduleMetadata = metadata
  }

  applyDataToTemplate(template: string, data: LooseObject): string {
    let strCurrentTemplate = cloneDeep(template);
    if (filled(template)) {

      const regex = new RegExp(`{{(.*?)}}`, 'gm');
      let matched = strCurrentTemplate.match(regex);
      if (filled(matched)) {
        matched.forEach( matched => {

          let strFieldKey = matched.replace(/({{)|(}})/g, '').trim();
          let objField = strFieldKey.split('.');
          let replacementValue = (filled(data[objField[0]]) && filled(data[objField[0]][objField[1]]))
            ? data[objField[0]][objField[1]]
            : '--';

          if (objField[1] === 'assigned_user') {
            replacementValue = get(replacementValue, 'display_name', '--');
          }
          // when the retrieved data is empty, check the data inside the metadata
          if (
            replacementValue === '--'
            && is_scheduler_item(data_get(data, objField[0]))
            && filled(data[objField[0]][objField[0]])
          ) {
            let objRawRecord = data[objField[0]][objField[0]];
            replacementValue = filled(objRawRecord[objField[1]]) ? objRawRecord[objField[1]] : '--';
          }

          strCurrentTemplate = strCurrentTemplate.replace(
            matched,
            this.getTemplateTranslation(objField[0], objField[1], replacementValue)
          );
        });
      }
    }

    return strCurrentTemplate.replace(/<p>/g, "<p style='margin-bottom: 0px !important;'>");
  }

  getTemplateTranslation(module: string, key: string, value: string): string {
    let mainModule = (module == 'task') ? 'activity' : module;

    // get translated value from dropdown options
    let translatedValue = get(this.translatedValue, `${pluralize(mainModule)}.${key}.${value}`);
    if (filled(translatedValue)) {
      return translatedValue;
    }

    // get the metadata and check the type if we need to convert the value
    let moduleMetadata = get(this.moduleMetadata, pluralize(mainModule));
    if (filled(moduleMetadata)) {

      let metadataField = get(moduleMetadata, key);
      if (filled(metadataField)) {
        if (metadataField.type == 'address' && isObject(value)) {
          return this.readableAddressPipe.transform(value) as string;
        }
        if (metadataField.type == 'checkbox') {
          return this.translateService.instant(value ? 'yes' : 'no');
        }
        if (metadataField.type == 'date') {
          return this.toReadableDate(value)
        }
        if (metadataField.type == 'datetime') {
          return this.toReadableDate(value, true)
        }
        if (metadataField.type == 'currency') {
            return new CustomCurrencyPipe(this.localStorageService).transform(Number(value));
        }
      }
    }

    return value;
  }

  getChangedMomentTime(date: moment.Moment, strTime: string): moment.Moment {
    const localMoment = date.clone().local();

    const [hour, minute] = strTime.split(':').map(
      (char: string) => Number(char)
    );

    return localMoment
      .hour(hour)
      .minute(minute)
      .seconds(0)
      .milliseconds(0);
  }

  /**
   * convert date to readable
   *
   * @param date
   * @param bDateTime
   * @returns
   */
  toReadableDate(date, bDateTime = false) {

    if (! date) {
      return '';
    }

    let convertedDate = moment(date);
    if (! convertedDate.isValid()) {
      return '';
    }

    if (bDateTime) {
      let convertedUTCDateTime = moment.utc(date).toDate();
      return moment(convertedUTCDateTime).format('lll');
    }

    return convertedDate.format('ll');
  }
}

type UnscheduleTaskResponse = {
  action: string;
  data: TaskMetadataC;
}

export const is_scheduler_item = (value: any): boolean => {
  if (blank(value)) {
    return false;
  }

  return value instanceof OpportunityMetadataC
    || value instanceof JobMetadataC;
};